import React from "react";
import { connect } from "react-redux";
import styled from "styled-components";

import { scrollToElement } from "../../../utils/scroll";
import { IStyle } from "../../../Types/Style";
import { ButtonsRow } from "../../Common/Components/FormComponents";
import * as Alert from "../../Common/Styles/Alert";
import { IState } from "../../reducer";
import { RequestState } from "../../types/common";
import { StyleLocation } from "../actions";
import { AddButton, WarnButton } from "./common/buttons";

const TextArea = styled.textarea`
  border: 0.1rem solid ${({ theme }) => theme.colors.grey};
  font-size: ${({ theme }) => theme.fontSizes.large};
  margin-bottom: 0.5rem;
  padding: 1rem;
  height: 30rem;
  width: 70%;
`;

interface ICustomLessCodeProps {
  style: IStyle;
  toggleMode: () => void;
  modifyLessCode: (lessCode: string) => void;
}

interface ICustomLessCodeStateProps {
  patchStyleStatus: RequestState;
  patchStyleLocation: StyleLocation;
  patchStyleError: string | null;
}

type CustomLessCodeProps = ICustomLessCodeStateProps & ICustomLessCodeProps;

const TITLE_ID = "lesscode-title";

interface IPatchAlertProps {
  status: RequestState;
}

const PatchAlert: React.FC<IPatchAlertProps> = ({ status }) => {
  React.useEffect(() => {
    if ([RequestState.FAILED, RequestState.SUCCEEDED].includes(status)) {
      scrollToElement(TITLE_ID);
    }
  });

  switch (status) {
    case RequestState.FAILED:
      return (
        <Alert.Failure>
          <Alert.FailureHeader>Failure</Alert.FailureHeader>
          <Alert.FailureMessage>
            The theme settings are not valid and will revert to the default theme. Please refer to the link in the Note
            for further information.
          </Alert.FailureMessage>
        </Alert.Failure>
      );
    case RequestState.SUCCEEDED:
      scrollToElement(TITLE_ID);
      return (
        <Alert.Success>
          <Alert.SuccessHeader>Success</Alert.SuccessHeader>
          <Alert.SuccessMessage>The themes settings have been saved successfully.</Alert.SuccessMessage>
        </Alert.Success>
      );
    default:
      return null;
  }
};

const CustomLessCode: React.FC<CustomLessCodeProps> = props => {
  const { style } = props;
  const textAreaRef: React.RefObject<HTMLTextAreaElement> = React.createRef();
  const modifyLessCode = () => {
    const textArea = textAreaRef.current;

    if (!textArea) {
      return;
    }

    props.modifyLessCode(textArea.value);
  };

  return (
    <>
      <h2 id={TITLE_ID}>Advanced theme settings</h2>
      {props.patchStyleLocation === "LessCode" && <PatchAlert status={props.patchStyleStatus} />}
      <Alert.Warning>
        <Alert.WarningHeader>Note</Alert.WarningHeader>
        <Alert.WarningMessage>
          Please, make sure to insert valid{" "}
          <a href="https://lesscss.org/features/" target="_blank">
            Less Code
          </a>{" "}
          in the text box below.
        </Alert.WarningMessage>
      </Alert.Warning>
      <TextArea name="" defaultValue={style.lessCode} ref={textAreaRef} />
      <ButtonsRow margin={1} width={30}>
        <AddButton onClick={modifyLessCode}>Apply Less Code</AddButton>
        <WarnButton onClick={props.toggleMode}>Use Colour Pickers</WarnButton>
      </ButtonsRow>
    </>
  );
};

const mapStateToProps = (state: IState): ICustomLessCodeStateProps => ({
  patchStyleError: state.App.ThemeManagement.patchStyleError,
  patchStyleLocation: state.App.ThemeManagement.patchStyleLocation,
  patchStyleStatus: state.App.ThemeManagement.patchStyleStatus,
});

export default connect(mapStateToProps)(CustomLessCode);
